/**
 * Section ordering for the document panels.
 *
 * Sections are stored with string numbers ("1", "2.1", "10", "A.1") and read
 * with Firestore orderBy('number'), which sorts them as text: "10" lands
 * before "2". Sort in memory with sortSections after every read.
 *
 * The rules are the generators' own, copied from
 * functions/src/utils/sectionOrder.ts compareSectionNumbers (web cannot
 * import functions code). sectionOrder.test.ts compares the two on a mixed
 * corpus, so a change to one that is not made to the other fails a test.
 */

/** rank 0 = numbered section, 1 = appendix, 2 = unnumbered text, 3 = missing. */
type Key = { rank: 0 | 1 | 2 | 3; letter: string; nums: number[]; text: string };

function sectionKey(n: unknown): Key {
  if (typeof n === 'number' && Number.isFinite(n)) return { rank: 0, letter: '', nums: [n], text: '' };
  if (n === null || n === undefined) return { rank: 3, letter: '', nums: [], text: '' };
  const str = String(n).trim().replace(/\.+$/, '');
  if (!str) return { rank: 3, letter: '', nums: [], text: '' };
  const m = /^(\d+(?:\.\d+)*)(?![\d])/.exec(str);
  if (m) return { rank: 0, letter: '', nums: m[1].split('.').map(Number), text: str.slice(m[1].length) };
  const ap = /^(?:Appendix\s+)?([A-Z])(?:\.(\d+(?:\.\d+)*))?(?![A-Za-z0-9_])/.exec(str);
  if (ap) return { rank: 1, letter: ap[1], nums: ap[2] ? ap[2].split('.').map(Number) : [], text: str.slice(ap[0].length) };
  return { rank: 2, letter: '', nums: [], text: str };
}

/**
 * Numbered sections by dotted parts, numerically ("2.1" < "10"; "1" < "1.1" <
 * "1.10"). Then appendices by letter, then numeric parts. Then unnumbered
 * text. Missing or empty last.
 */
export function compareSectionNumbers(a: unknown, b: unknown): number {
  const ka = sectionKey(a);
  const kb = sectionKey(b);
  if (ka.rank !== kb.rank) return ka.rank - kb.rank;
  if (ka.letter !== kb.letter) return ka.letter < kb.letter ? -1 : 1;
  const n = Math.max(ka.nums.length, kb.nums.length);
  for (let i = 0; i < n; i++) {
    const x = ka.nums[i];
    const y = kb.nums[i];
    if (x === undefined) return -1;
    if (y === undefined) return 1;
    if (x !== y) return x - y;
  }
  return ka.text < kb.text ? -1 : ka.text > kb.text ? 1 : 0;
}

/** A sorted copy (stable); the input is not changed. */
export function sortSections<T extends { number?: unknown }>(list: readonly T[]): T[] {
  return list
    .map((item, i) => ({ item, i }))
    .sort((x, y) => compareSectionNumbers(x.item.number, y.item.number) || x.i - y.i)
    .map((x) => x.item);
}
